import React, { useState, useEffect } from 'react';
import httpClient from '../services/httpClient';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';

const HourlyStatistics = () => {
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const COLORS = ['#091C36', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

  useEffect(() => {
    loadStatistics();
  }, [selectedDate]);

  const loadStatistics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await httpClient.get('/api/accounts/hourly-statistics/', {
        params: { date: selectedDate }
      });
      setData(response.data);
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
      setError('Не удалось загрузить статистику');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadStatistics}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Попробовать снова
        </button>
      </div>
    );
  }

  if (!data) return null;

  // Подготовка данных для графиков
  const revenueData = data.hourly_data.filter(h => h.orders_count > 0);
  
  // Данные для круговой диаграммы способов оплаты
  const totalCash = data.hourly_data.reduce((sum, h) => sum + h.payment_methods.cash, 0);
  const totalCard = data.hourly_data.reduce((sum, h) => sum + h.payment_methods.card, 0);
  const paymentMethodsData = [
    { name: 'Наличные', value: totalCash },
    { name: 'Карта', value: totalCard }
  ];

  // Данные для круговой диаграммы типов заказов
  const totalDineIn = data.hourly_data.reduce((sum, h) => sum + h.order_types.dine_in, 0);
  const totalTakeaway = data.hourly_data.reduce((sum, h) => sum + h.order_types.takeaway, 0);
  const totalDelivery = data.hourly_data.reduce((sum, h) => sum + h.order_types.delivery, 0);
  const orderTypesData = [
    { name: 'В заведении', value: totalDineIn },
    { name: 'Вынос', value: totalTakeaway },
    { name: 'Доставка', value: totalDelivery }
  ];

  return (
    <div className="space-y-6">
      {/* Заголовок и фильтр по дате */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Почасовая статистика</h1>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Дата:</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </label>
            <button
              onClick={loadStatistics}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Обновить
            </button>
          </div>
        </div>
      </div>

      {/* Сводка за день */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Всего заказов</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{data.summary.total_orders}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Общая выручка</div>
          <div className="text-3xl font-bold text-green-600 mt-2">
            {data.summary.total_revenue.toLocaleString('ru-RU')} ₽
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Средний чек</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">
            {Math.round(data.summary.avg_check).toLocaleString('ru-RU')} ₽
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-sm font-medium text-gray-600">Пиковый час</div>
          <div className="text-3xl font-bold text-orange-600 mt-2">
            {data.summary.peak_hour !== null ? `${data.summary.peak_hour}:00` : 'Н/Д'}
          </div>
        </div>
      </div>

      {/* График выручки по часам */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Выручка по часам</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.hourly_data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" label={{ value: 'Час', position: 'insideBottom', offset: -5 }} />
            <YAxis label={{ value: 'Выручка (₽)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => `${value.toLocaleString('ru-RU')} ₽`} />
            <Legend />
            <Line type="monotone" dataKey="total_revenue" stroke="#091C36" strokeWidth={2} name="Выручка" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* График количества заказов по часам */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Количество заказов по часам</h2>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.hourly_data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" label={{ value: 'Час', position: 'insideBottom', offset: -5 }} />
            <YAxis label={{ value: 'Заказов', angle: -90, position: 'insideLeft' }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="orders_count" fill="#3b82f6" name="Заказы" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* График среднего чека по часам */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Средний чек по часам</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={revenueData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="hour" label={{ value: 'Час', position: 'insideBottom', offset: -5 }} />
            <YAxis label={{ value: 'Средний чек (₽)', angle: -90, position: 'insideLeft' }} />
            <Tooltip formatter={(value) => `${Math.round(value).toLocaleString('ru-RU')} ₽`} />
            <Legend />
            <Line type="monotone" dataKey="avg_check" stroke="#10b981" strokeWidth={2} name="Средний чек" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Круговые диаграммы */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Способы оплаты */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Способы оплаты</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={paymentMethodsData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {paymentMethodsData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 flex justify-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[0] }}></div>
              <span className="text-sm">Наличные: {totalCash}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[1] }}></div>
              <span className="text-sm">Карта: {totalCard}</span>
            </div>
          </div>
        </div>

        {/* Типы заказов */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Типы заказов</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={orderTypesData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {orderTypesData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 flex justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[0] }}></div>
              <span className="text-sm">В заведении: {totalDineIn}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[1] }}></div>
              <span className="text-sm">Вынос: {totalTakeaway}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: COLORS[2] }}></div>
              <span className="text-sm">Доставка: {totalDelivery}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Таблица детальных данных */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Детальная информация</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Час</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Заказов</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Выручка</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Средний чек</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Нал/Карта</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Зал/Вынос/Доставка</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.hourly_data.filter(h => h.orders_count > 0).map((hour) => (
                <tr key={hour.hour} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {hour.hour_label}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {hour.orders_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {hour.total_revenue.toLocaleString('ru-RU')} ₽
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {Math.round(hour.avg_check).toLocaleString('ru-RU')} ₽
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {hour.payment_methods.cash} / {hour.payment_methods.card}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {hour.order_types.dine_in} / {hour.order_types.takeaway} / {hour.order_types.delivery}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HourlyStatistics;

